import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { t, locale, formatDate } from '../i18n';
import { Card, Tag, Rating, SectionTitle, EmptyState } from '../ui';
import ReviewCard from './ReviewCard';
import StatsPanel from './StatsPanel';
import Lightbox from './Lightbox';

/** Badge de formule premium (bronze / silver / gold / platinum). */
export function PremiumBadge({ abo }) {
    if (!abo || !abo.type_premium) return null;
    return (
        <Tag variant="gradient">
            <img src={`/img/premium_${abo.type_premium}.svg`} alt="" style={{ height: 14 }} />
            {abo.type_premium.charAt(0).toUpperCase() + abo.type_premium.slice(1)}
            {abo.until && <> · {t('jusquau')} {formatDate(abo.until)}</>}
        </Tag>
    );
}

/** Carte principale d'un lieu sur le dashboard. */
export default function PlaceCard({ place }) {
    const [lightbox, setLightbox] = useState(null);

    return (
        <Card>
            <div className="card__header" style={{ alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    {place.type_code && (
                        <img
                            src={`/img/access/icon_${place.type_code.toLowerCase()}.svg`}
                            alt={place.type_code}
                            style={{ width: 40, height: 40 }}
                        />
                    )}
                    <div style={{ minWidth: 0 }}>
                        <h2 className="section-title truncate" style={{ margin: 0 }}>
                            {place.title}
                        </h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                            {place.note_moyenne != null
                                ? <Rating value={place.note_moyenne} />
                                : <span className="text-muted text-sm">{t('no_review')}</span>}
                            <Link to={`/commentaires/${place.id}`} className="text-sm" style={{ color: 'var(--p4n-blue)' }}>
                                {place.nb_commentaires
                                    ? `${place.nb_commentaires} ${t('mes_commentaires')}`
                                    : t('no_comment')}
                            </Link>
                            <PremiumBadge abo={place.abo} />
                        </div>
                    </div>
                </div>
                <div className="toolbar" style={{ flexWrap: 'nowrap' }}>
                    <Link to={`/edit/${place.id}`} className="btn btn--primary btn--sm">{t('modifier')}</Link>
                    <a
                        href={`https://park4night.com/${locale}/place/${place.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn--secondary btn--sm"
                    >
                        {t('voir')}
                    </a>
                </div>
            </div>

            {place.images.length > 0 && (
                <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, marginBottom: 16 }}>
                    {place.images.slice(0, 5).map((image, i) => (
                        <img
                            key={image.id}
                            src={image.url}
                            alt=""
                            onClick={() => setLightbox(i)}
                            style={{ height: 120, borderRadius: 'var(--radius-md)', cursor: 'zoom-in', flexShrink: 0 }}
                        />
                    ))}
                </div>
            )}
            <Lightbox images={place.images} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 32 }}>
                <div>
                    <SectionTitle>{t('desc')}</SectionTitle>
                    <p className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>
                        {place.description || <span className="text-muted">—</span>}
                    </p>
                </div>
                <div>
                    <div className="section-head">
                        <SectionTitle>{t('lastComm')}</SectionTitle>
                        <Link to={`/commentaires/${place.id}`} className="text-sm" style={{ color: 'var(--p4n-blue)' }}>
                            {t('see_all')}
                        </Link>
                    </div>
                    {place.reviews.length === 0 && <EmptyState>{t('no_comment')}</EmptyState>}
                    {place.reviews.slice(0, 2).map((review) => (
                        <ReviewCard key={review.id} review={review} />
                    ))}
                </div>
            </div>

            <div style={{ marginTop: 32 }}>
                <StatsPanel placeId={place.id} />
            </div>
        </Card>
    );
}
